import { FC } from 'react';

import { Box, useTheme } from '@mui/material';
import { css, styled } from '@mui/material/styles';

import { isEqual } from 'lodash/fp';

import {
  DuplicatePatientData,
  MergePatientsModalColumn
} from 'views/Modals/MergePatientsModal/MergePatientsModal.types';

import { Text } from 'components/UIkit/atoms/Text';

interface MergePatientsModalContentProps {
  columns: MergePatientsModalColumn[];
  duplicatePatientData: DuplicatePatientData[];
}

export const MergePatientsModalContent: FC<MergePatientsModalContentProps> = ({
  columns,
  duplicatePatientData
}) => {
  const { palette } = useTheme();
  const getColumnColor = (key: keyof DuplicatePatientData) => {
    if (key === 'type' || key === 'id') {
      return palette.common.white;
    }

    if (key === 'mrn' || duplicatePatientData.length < 2) {
      return palette.natural.inactiveBackground;
    }

    const column = columns.find((column) => column.accessor === key);

    const firstKeyAsString = String(duplicatePatientData[0][key]);
    const secondKeyAsString = String(duplicatePatientData[1][key]);
    const isValuesEqual = column?.compareFn
      ? column.compareFn(firstKeyAsString, secondKeyAsString)
      : isEqual(firstKeyAsString?.toLowerCase(), secondKeyAsString?.toLowerCase());

    return isValuesEqual ? palette.primary.light : palette.tag.highRisk;
  };

  return (
    <Box mb={40}>
      <StyledHeaderRow>
        {columns.map(({ name, accessor, width }) => (
          <StyledHeaderCell key={accessor} color={getColumnColor(accessor)} width={width}>
            <Text>{name}</Text>
          </StyledHeaderCell>
        ))}
      </StyledHeaderRow>

      <StyledRowContainer>
        {duplicatePatientData.map((patientRow, index) => (
          <StyledRow key={patientRow.id}>
            {columns.map(({ accessor, Cell, formatCell, width = '123px', align = 'center' }) => {
              if (Cell) {
                return Cell(String(patientRow[accessor]));
              }

              const cellContent = formatCell
                ? formatCell(String(patientRow[accessor]))
                : patientRow[accessor];

              return (
                <Text
                  key={accessor}
                  width={width}
                  textAlign={align}
                  variant="body2"
                  py={12}
                  title={String(cellContent)}
                >
                  {cellContent || '—'}
                </Text>
              );
            })}
          </StyledRow>
        ))}
      </StyledRowContainer>
    </Box>
  );
};

const StyledRowContainer = styled(Box)(
  ({ theme }) => css`
    div:nth-child(even) {
      background-color: ${theme.palette.natural.inactiveBackground};
    }
  `
);

const StyledRow = styled(Box)(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(24)};
    padding: ${theme.spacing(0, 8)};
    border-radius: ${theme.borderRadius.small};
  `
);

const StyledHeaderRow = styled(StyledRow)(
  ({ theme }) => css`
    margin-bottom: ${theme.spacing(12)};
  `
);

const StyledHeaderCell = styled(Box)<{
  color: string;
  width?: string;
}>(
  ({ theme, color, width = '123px' }) =>
    css`
      background-color: ${color};
      text-align: center;
      width: ${width};
      height: 21px;
      border-radius: ${theme.borderRadius.medium};
    `
);
